import React, { useState } from 'react';
import { Mail, MessageCircle, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    toast.success("Message sent! We'll get back to you soon!");
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <Mail className="w-16 h-16 mx-auto mb-6 text-yellow-400" />
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Get in Touch</h1>
          <p className="text-xl text-purple-200 max-w-2xl mx-auto">
            Have questions about our ritual oils? We'd love to hear from you!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="bg-purple-800/50 border-purple-600">
            <CardContent className="pt-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-purple-200 mb-2 block">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-purple-200 mb-2 block">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="subject" className="text-purple-200 mb-2 block">Subject</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-purple-200 mb-2 block">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="bg-purple-900/50 border-purple-600 text-white placeholder:text-purple-400 resize-none"
                    placeholder="Tell us about your spiritual journey..."
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-purple-900 font-semibold"
                >
                  <Send className="mr-2" />
                  Send Message
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="bg-purple-800/50 border-purple-600">
              <CardContent className="pt-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Email Us</h3>
                    <p className="text-purple-200">daizyinfused@gmail.com</p>
                    <p className="text-purple-300 text-sm mt-2">
                      We typically respond within 24-48 hours
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-purple-800/50 border-purple-600">
              <CardContent className="pt-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Custom Orders</h3>
                    <p className="text-purple-200">
                      Looking for a custom blend or intention oil? We'd be happy to create something special just for you!
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-purple-800/50 border-purple-600">
              <CardContent className="pt-8">
                <h3 className="text-xl font-semibold text-white mb-4">FAQ</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-white font-medium mb-1">How long do oils last?</p>
                    <p className="text-purple-200 text-sm">When stored properly in glass bottles away from direct sunlight, our oils maintain potency for 1-2 years.</p>
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Are your products vegan?</p>
                    <p className="text-purple-200 text-sm">Yes! All our oils are 100% vegan and cruelty-free, using only plant-based ingredients and crystal essences.</p>
                  </div>
                  <div>
                    <p className="text-white font-medium mb-1">Can I use these oils on skin?</p>
                    <p className="text-purple-200 text-sm">All my products are safe for skin. Do a patch test first to be sure you are not allergic to any of the ingredients.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
